import React, { FC, useEffect, useState } from 'react';
import { useProfessions } from '../../hooks/useProfessions';
import { IProfession } from '../../models/Profession.interface';
import { MainContainer } from '../../components/ContentWrapper';
import { DotLottleLoader } from '../../components/Loader';
import { PageTitle } from '../../components/PageTitle';
import { AreaOfFocusProfessionItem } from './AreaOfFocusProfessionItem';

// Read-only listing of Areas of Focus grouped by profession. Areas of Focus are
// L1-scoped (a third taxonomy level under the profession), so they hang off each
// profession — mirroring the Professions page layout. Data comes from the same
// /professions/tree feed (areaOfFocusLibrary keyed by profession id).
export const AreasOfFocusPage: FC = () => {
  const {
    professionsList,
    areaOfFocusLibrary,
    getProfessions,
    areProfessionsLoaded,
  } = useProfessions();
  const [initialLoaded, setInitialLoaded] = useState<boolean>(professionsList.length > 0);

  useEffect(() => {
    if (!areProfessionsLoaded) getProfessions();
  }, [areProfessionsLoaded]);

  useEffect(() => {
    if (professionsList.length > 0 || areProfessionsLoaded) setInitialLoaded(true);
  }, [professionsList, areProfessionsLoaded]);

  const professions: IProfession[] = professionsList;
  const totalAreas = professions.reduce(
    (acc, p) => acc + (areaOfFocusLibrary[p.id]?.length ?? 0),
    0,
  );

  if (!initialLoaded) {
    return (
      <MainContainer>
        <DotLottleLoader fill={false} />
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
        <div>
          <PageTitle title="Areas of Focus" />
          <p className="text-[13px] text-text-2 mt-1">
            {professions.length} categories · {totalAreas} areas of focus
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-2 pb-6">
        {professions.map((profession) => (
          <AreaOfFocusProfessionItem
            key={profession.id}
            profession={profession}
            areasOfFocus={areaOfFocusLibrary[profession.id] ?? []}
          />
        ))}
      </div>
    </MainContainer>
  );
};
